import {
  memo, useMemo, type JSX,
} from 'react';
import { Box, styled } from '@mui/material';
import type {
  ChartAssay,
  IAssayFilterState,
  IChartLaneLogScale,
  IDiseaseBurdenAnalyte,
  InformedMode,
} from '@/types/PatientDashboard/PatientTimeline.types';
import AnalyteControlPanel from './AnalyteControlPanel';
import BurdenChart from './BurdenChart';
import { ChartLaneRow, LANE_BORDERS } from './ChartLane.styles';
import { getDiseaseBurdenLines } from './DiseaseBurden.util';

const CollapsedTrack = styled(Box)({
  flex: 1,
  minWidth: 0,
  ...LANE_BORDERS,
});

interface IProps {
  analyte: IDiseaseBurdenAnalyte;
  filters: Record<string, IAssayFilterState> | undefined;
  isVisible: boolean;
  scale: IChartLaneLogScale;
  onToggleAssay: (analyte: string, assay: ChartAssay) => void;
  onSetMode: (analyte: string, assay: ChartAssay, mode: InformedMode) => void;
  onToggleVisibility: (analyte: string) => void;
  xPct: (date: string) => number;
}

function AnalyteRow({
  analyte,
  filters,
  isVisible,
  scale,
  onToggleAssay,
  onSetMode,
  onToggleVisibility,
  xPct,
}: IProps): JSX.Element {
  const lines = useMemo(
    () => (isVisible ? getDiseaseBurdenLines(analyte, filters) : []),
    [analyte, filters, isVisible],
  );

  return (
    <ChartLaneRow>
      <AnalyteControlPanel
        analyte={analyte}
        filters={filters}
        isVisible={isVisible}
        onToggleAssay={onToggleAssay}
        onSetMode={onSetMode}
        onToggleVisibility={onToggleVisibility}
      />
      {isVisible ? (
        <BurdenChart
          lines={lines}
          colour={analyte.colour}
          scale={scale}
          xPct={xPct}
        />
      ) : <CollapsedTrack />}
    </ChartLaneRow>
  );
}

export default memo(AnalyteRow);
